import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import { msalInstance } from './auth/msalConfig';
import { QueryProvider } from './app/QueryProvider';
import { BrandedThemeProvider } from './app/BrandedThemeProvider';
import { ToastProvider } from './components';
import { AppRoutes } from './app/routes';
import { SessionExpiredHost } from './auth/SessionExpiredHost';

/**
 * Composition root for the SPA. Provider ordering is fixed (WEB PRD §2.1):
 *  1. `MsalProvider` so any descendant can resolve the MSAL instance.
 *  2. `QueryProvider` for TanStack Query (all server state).
 *  3. `BrandedThemeProvider` (+ its `CssBaseline`) so MUI has the single
 *     application theme before anything renders.
 *  4. `ToastProvider` — the shared kit's toast host, mounted once so any screen
 *     can push toasts via `useToast()`.
 *  5. `BrowserRouter` + the route tree last, so the providers above are
 *     established before any route component mounts.
 *
 * <p>**Why theme sits below query.** Per-customer branding (WEB PRD §2.6) reads
 * `whoami` to resolve the effective customer's brand colors, so the theme
 * provider needs the query client above it. This inverts the theme/query order
 * that held before branding was applied. It is safe because `BrandedThemeProvider`
 * always renders a valid theme — the unbranded Polaris default until (and unless)
 * a customer resolves — so no descendant ever sees a missing theme.</p>
 *
 * The route tree (`AppRoutes`) owns public vs. shell/guarded routing.
 * `SessionExpiredHost` is a routeless singleton so the re-auth interstitial can
 * overlay any route (the shell on a mid-session renewal failure, the neutral
 * bootstrap card on a first-load one).
 */
export function App() {
  return (
    <MsalProvider instance={msalInstance}>
      <QueryProvider>
        <BrandedThemeProvider>
          <ToastProvider>
            <SessionExpiredHost />
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </ToastProvider>
        </BrandedThemeProvider>
      </QueryProvider>
    </MsalProvider>
  );
}
